import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, X, Users, CalendarCheck, Phone, UserRoundSearch } from 'lucide-react';
import Layout from '../components/Layout';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import TraitChip from '../components/customers/TraitChip';
import ConsentBadge from '../components/customers/ConsentBadge';
import CustomerProfilePane from '../components/customers/CustomerProfilePane';
import { useDebounce } from '../hooks/useDebounce';
import authService, { AgentNumberConfig } from '../services/authService';
import customerService, {
  CustomerDetail,
  CustomerListItem,
  CustomerSort,
  TimelineEntry,
} from '../services/customerService';

const SORTS: { id: CustomerSort; label: string }[] = [
  { id: 'last_contact', label: 'Zuletzt aktiv' },
  { id: 'bookings', label: 'Meiste Buchungen' },
  { id: 'name', label: 'Name' },
];

function formatDate(ts: string | null): string {
  if (!ts) return 'Noch kein Kontakt';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function numOrNull(v: string | null): number | null {
  if (!v) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function avatarInitial(name: string | null, phone: string | null): string {
  return (name?.trim()?.[0] ?? phone?.slice(-2, -1) ?? '#').toUpperCase();
}

/** Desktop shows the profile in-pane; mobile navigates to its own route. */
function useIsDesktop(): boolean {
  const query = '(min-width: 768px)';
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

export default function CustomersPage() {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState<string>(() => searchParams.get('q') ?? '');
  const debouncedSearch = useDebounce(searchInput, 300);
  const [sort, setSort] = useState<CustomerSort>('last_contact');
  const [onlyConsented, setOnlyConsented] = useState(false);

  const [items, setItems] = useState<CustomerListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<number | null>(() => numOrNull(searchParams.get('k')));
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Agent filter — only meaningful for tenants running several numbers.
  const [agents, setAgents] = useState<AgentNumberConfig[]>([]);
  const [agentFilter, setAgentFilter] = useState<string>('');

  useEffect(() => {
    authService
      .getNumberConfigs()
      .then((res) => setAgents(res.numbers || []))
      .catch(() => setAgents([]));
  }, []);

  const loadList = useCallback(
    async (q: string, agent: string, sortBy: CustomerSort, consented: boolean) => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await customerService.list({
          q,
          agentNumber: agent || undefined,
          consent: consented ? 'granted' : undefined,
          sort: sortBy,
          limit: 50,
        });
        setItems(res.items || []);
        setTotal(res.total || 0);
      } catch {
        setError('Kunden konnten nicht geladen werden.');
        setItems([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    loadList(debouncedSearch, agentFilter, sort, onlyConsented);
  }, [debouncedSearch, agentFilter, sort, onlyConsented, loadList]);

  // Keep the URL shareable and the back button useful.
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedSearch.trim()) next.set('q', debouncedSearch.trim());
    if (selectedId != null) next.set('k', String(selectedId));
    setSearchParams(next, { replace: true, preventScrollReset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedId]);

  useEffect(() => {
    if (selectedId == null) {
      setDetail(null);
      setTimeline([]);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    (async () => {
      try {
        const res = await customerService.getDetail(selectedId);
        if (cancelled) return;
        setDetail(res.customer);
        setTimeline(res.timeline || []);
      } catch {
        if (cancelled) return;
        setDetailError('Kundenprofil konnte nicht geladen werden.');
        setDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const handleSelect = (item: CustomerListItem) => {
    if (isDesktop) {
      setSelectedId(item.id);
    } else {
      navigate(`/customers/${item.id}`);
    }
  };

  const listPane = (
    <div className="flex h-full min-h-0 flex-col">
      <div className="relative mb-3 flex-shrink-0">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Name oder Nummer suchen…"
          className="pl-9 pr-9"
        />
        {searchInput && (
          <button
            onClick={() => setSearchInput('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
            aria-label="Suche löschen"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="mb-3 flex flex-shrink-0 items-center gap-1 border-b border-border">
        {SORTS.map((option) => (
          <button
            key={option.id}
            onClick={() => setSort(option.id)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              sort === option.id
                ? 'border-[#1967D2] text-[#1967D2]'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="mb-2 flex flex-shrink-0 items-center gap-2">
        <Checkbox
          id="only-consented"
          checked={onlyConsented}
          onCheckedChange={(checked) => setOnlyConsented(checked === true)}
        />
        <Label
          htmlFor="only-consented"
          className="cursor-pointer text-xs font-normal text-muted-foreground"
        >
          Nur Kunden mit Werbe-Einwilligung
        </Label>
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))
        ) : error ? (
          <div className="py-16 text-center text-sm text-muted-foreground">{error}</div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <UserRoundSearch className="mb-2 h-9 w-9 opacity-20" />
            <p className="text-sm">
              {onlyConsented
                ? 'Noch niemand hat der Werbung zugestimmt'
                : debouncedSearch.trim()
                  ? 'Keine Treffer'
                  : 'Noch keine Kunden erfasst'}
            </p>
          </div>
        ) : (
          items.map((item) => {
            const isActive = isDesktop && item.id === selectedId;
            const title = item.name?.trim() || (item.phone ? `+${item.phone}` : 'Unbekannt');
            return (
              <button
                key={item.id}
                onClick={() => handleSelect(item)}
                className={cn(
                  'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-all',
                  isActive
                    ? 'border-[#1967D2] bg-primary/10 shadow-sm'
                    : 'border-border bg-card hover:border-foreground/20 hover:shadow-sm',
                )}
              >
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary-strong">
                  {avatarInitial(item.name, item.phone)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="truncate font-medium text-foreground">{title}</h4>
                    {item.consent_status === 'granted' && (
                      <ConsentBadge status="granted" className="flex-shrink-0" />
                    )}
                  </div>
                  {item.name?.trim() && item.phone && (
                    <p className="truncate font-mono text-xs text-muted-foreground">+{item.phone}</p>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <CalendarCheck className="h-3 w-3" aria-hidden />
                      {item.bookings_total} Buchungen
                    </span>
                    <span className="flex items-center gap-1">
                      <Phone className="h-3 w-3" aria-hidden />
                      {item.calls_total} Anrufe
                    </span>
                    <span>{formatDate(item.last_contact_at)}</span>
                  </div>
                  {item.traits.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {item.traits.slice(0, 3).map((trait) => (
                        <TraitChip key={trait.trait} trait={trait} />
                      ))}
                    </div>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  const detailPane = (
    <div className="flex h-full min-h-0 flex-col">
      {detailLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-16 w-1/2 rounded-lg" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      ) : detailError ? (
        <div className="py-16 text-center text-muted-foreground">{detailError}</div>
      ) : !detail ? (
        <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
          <Users className="mb-3 h-12 w-12 opacity-15" />
          <p className="text-sm">Wähle links einen Kunden aus</p>
        </div>
      ) : (
        <CustomerProfilePane customer={detail} timeline={timeline} />
      )}
    </div>
  );

  return (
    <Layout>
      <div className="mx-auto flex h-full min-h-0 w-full max-w-6xl flex-col gap-6 py-6 animate-in fade-in duration-500 md:overflow-hidden">
        <div className="flex-shrink-0 border-b pb-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground">Kunden</h1>
              <p className="mt-1 text-muted-foreground">
                {total > 0
                  ? `${total} Kund${total === 1 ? 'e' : 'en'} aus Anrufen und WhatsApp – mit Buchungen, Vorlieben und Einwilligung`
                  : 'Kunden aus Anrufen und WhatsApp – mit Buchungen, Vorlieben und Einwilligung'}
              </p>
            </div>
            {agents.length > 1 && (
              <select
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
                className="mt-1 w-56 flex-shrink-0 rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Alle Agenten</option>
                {agents.map((agent) => (
                  <option key={agent.phone_number} value={agent.phone_number}>
                    {agent.display_name || agent.phone_number}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="flex gap-5 md:min-h-0 md:flex-1">
          <div className="min-h-0 w-full md:w-80 md:flex-shrink-0 lg:w-96">{listPane}</div>
          <div className="hidden min-h-0 min-w-0 flex-1 overflow-hidden rounded-xl border border-border bg-card/60 p-5 md:flex md:flex-col">
            {detailPane}
          </div>
        </div>
      </div>
    </Layout>
  );
}
